import type { PlayerBaseOptions } from '@racemap/sdk/schema/player';
import { NULL_TAG } from '@racemap/utilities/consts/events';
import { clamp } from '@racemap/utilities/math';
import type { UnitType } from '@racemap/utilities/types/units';
import { castDraft, type Draft, Immer } from 'immer';
import isEqual from 'lodash/isEqual';
import { unstable_batchedUpdates } from 'react-dom';
import type { StoreApi } from 'zustand';
import { SHADOW_TRACK_STEP, TIME_MODES } from './constants';
import type { HashContent } from './controller/HashManager';
import type { EventStateWithOptions } from './parse';
import {
  getCurrentEvents,
  reduceEndTime,
  reduceMax,
  reduceMin,
  reduceStartTime,
} from './selectors';
import {
  type EventState,
  type FlagContent,
  type GeolocationStatus,
  type LatLngOpacity,
  MapType,
  PinToShadowTrackMode,
  type Point,
  type StarterType,
  type State,
  type TimeMode,
} from './store';
import { difference, iteratorFind, toggleThroughArray, union } from './utils';
import { correctedNow, isLiveNow } from './utils/clock';

const EPSILON = 100;

// ponytail: no autoFreeze, the store holds large event data and was never frozen
const { produce } = new Immer({ autoFreeze: false });

function cleanEventIndices(eventCount: number, eventIndices: Array<number>) {
  return [
    ...new Set(eventIndices.filter((a) => !Number.isNaN(a) && a >= 0 && a < eventCount)),
  ].sort();
}

function setTimerTime(state: State, time: number): State {
  const currentEvents = getCurrentEvents(state);
  const currentStartTime = reduceStartTime(currentEvents);
  const currentEndTime = reduceEndTime(currentEvents);

  const liveMark = correctedNow() - reduceMax(currentEvents, (ev) => ev.options.liveDelay);

  const isLive = time >= liveMark - EPSILON;
  let newTime = Math.min(liveMark, time, currentEndTime);

  if (currentStartTime <= correctedNow()) {
    newTime = clamp(currentStartTime, newTime, currentEndTime);
  }

  if (state.timer.time === newTime && state.timer.isLive === isLive) {
    return state;
  }

  return produce(state, (d) => {
    d.timer.time = newTime;
    d.timer.isLive = isLive;
  });
}

function switchEvent(state: State, currentEventIndices: Array<number>): State {
  let cleanedCurrentEventIndices = cleanEventIndices(state.events.length, currentEventIndices);
  if (cleanedCurrentEventIndices.length === 0) {
    cleanedCurrentEventIndices = getDefaultCurrentEventIds(state.events);
  }
  if (!isEqual(cleanedCurrentEventIndices, state.currentEventIndices)) {
    const currentEvents = cleanedCurrentEventIndices.map((i) => state.events[i]);
    const selectedIds = state.timer.selected.filter((selectedId) =>
      currentEvents.some(
        (ev) =>
          ev.starters.some((s) => s.id === selectedId) ||
          ev.pois.some((p) => p.id === selectedId) ||
          ev.tracks.some((t) => t.id === selectedId),
      ),
    );
    return setTimerTime(
      produce(state, (d) => {
        d.currentEventIndices = cleanedCurrentEventIndices;
        d.timer.selected = selectedIds;
      }),
      state.timer.time,
    );
  }
  return state;
}

function getAvailableTagHashes(state: State): Set<string> {
  const output = new Set<string>();
  for (const event of state.events) {
    for (const starter of event.starters) {
      for (const [key, value] of starter.tags) {
        output.add(`${key}:${value}`);
        output.add(`${key}:${NULL_TAG}`);
      }
    }
  }
  return output;
}

function flattenTags(tagMap: Map<string, Array<string>>): Array<[string, string]> {
  const output: Array<[string, string]> = [];
  for (const [key, values] of tagMap) {
    for (const value of values) {
      output.push([key, value]);
    }
  }
  return output;
}

function unflattenTags(tagList: Array<[string, string]>): Map<string, Array<string>> {
  const output: Map<string, Array<string>> = new Map();
  for (const [key, value] of tagList) {
    if (output.has(key)) {
      output.get(key)?.push(value);
    } else {
      output.set(key, [value]);
    }
  }
  return output;
}

function onlyAvailableTagGroups(
  selectedTags: Map<string, Array<string>>,
  availableTags: Set<string>,
): Map<string, Array<string>> {
  return unflattenTags(
    flattenTags(selectedTags).filter(([key, value]) => availableTags.has(`${key}:${value}`)),
  );
}

function setForEvents(newState: State, updater: (event: Draft<EventState>) => void): State {
  return produce(newState, (d) => {
    for (const event of d.events) updater(event);
  });
}

function applyHashOverrides(state: State, hash: HashContent): State {
  let newState = state;
  // Applying override from hash
  const { showAllFlags, largeMode, mapOnly, flagContent, selectedFlagContent } = hash;
  if (showAllFlags != null) {
    newState = produce(newState, (d) => {
      d.options.showAllFlags = showAllFlags;
    });
  }
  if (largeMode != null) {
    newState = produce(newState, (d) => {
      d.options.largeMode = largeMode;
    });
  }
  if (mapOnly != null) {
    newState = produce(newState, (d) => {
      d.options.mapOnly = mapOnly;
    });
  }
  if (flagContent != null) {
    newState = produce(newState, (d) => {
      d.options.overrideFlagContent = flagContent;
    });
  }
  if (selectedFlagContent != null) {
    newState = produce(newState, (d) => {
      d.options.overrideSelectedFlagContent = selectedFlagContent;
    });
  }
  const { listOpen, infoTabsDefaultTab, infoTabsDisabledTabs, infoTabsOpenByDefault } = hash;
  if (listOpen != null) {
    newState = setForEvents(newState, (ev) => {
      ev.options.listOpenByDefault = listOpen;
    });
  }
  if (infoTabsDefaultTab != null) {
    newState = setForEvents(newState, (ev) => {
      ev.options.infoTabs.defaultTab = infoTabsDefaultTab;
    });
  }
  if (infoTabsDisabledTabs != null) {
    newState = setForEvents(newState, (ev) => {
      ev.options.infoTabs.disabledTabs = infoTabsDisabledTabs;
    });
  }
  if (infoTabsOpenByDefault != null) {
    newState = setForEvents(newState, (ev) => {
      ev.options.infoTabs.openByDefault = infoTabsOpenByDefault;
    });
  }
  if (hash.hideNonSelected === true) {
    let selectedIds: Array<string> = [];
    if (hash.selected != null) {
      selectedIds = hash.selected;
    }
    if (hash.selectedStartNumber != null) {
      selectedIds = selectedIds.concat(
        hash.selectedStartNumber
          .map((startNumber) =>
            iteratorFind(
              newState.startersById.entries(),
              ([, starters]: [string, Array<StarterType>]) =>
                starters.some((starter: StarterType) => starter.startNumber === startNumber),
            ),
          )
          .filter((item) => item != null)
          .map(([starterId]) => starterId),
      );
    }
    const hidden = new Set(
      Array.from(newState.startersById.keys()).filter((id) => !selectedIds.includes(id)),
    );
    newState = produce(newState, (d) => {
      d.timer.hidden = hidden;
    });
  }
  const { mapStyle, zoom, viewport } = hash;
  if (mapStyle != null) {
    newState = produce(newState, (d) => {
      d.options.mapType = MapType[mapStyle.toUpperCase() as keyof typeof MapType];
    });
  } else if (newState.options.mapType === MapType.ROADMAP) {
    newState = produce(newState, (d) => {
      d.options.mapType = MapType.TERRAIN;
    });
  }
  if (zoom != null) {
    newState = produce(newState, (d) => {
      d.options.overrideZoom = zoom;
    });
  }
  if (viewport != null) {
    newState = produce(newState, (d) => {
      d.options.overrideViewport = viewport;
    });
  }
  return newState;
}

function setSelectedTags(
  state: State,
  selectedTags: Map<string, Array<string>>,
  { overwriteDefault = false }: { overwriteDefault?: boolean } = {},
): State {
  const availableTags = getAvailableTagHashes(state);
  const onlyAvailableSelectedTags = onlyAvailableTagGroups(selectedTags, availableTags);

  return produce(state, (d) => {
    d.timer.selectedTags = onlyAvailableSelectedTags;
    // overwrite default selected tags if specified, otherwise keep the existing default selected tags
    if (overwriteDefault) d.timer.defaultSelectedTags = onlyAvailableSelectedTags;
  });
}

function getDefaultCurrentEventIds(events: Array<EventState>): Array<number> {
  const currentlyRunningEvents = events.filter(
    (ev) => correctedNow() >= ev.startTime && ev.endTime >= correctedNow(),
  );
  if (currentlyRunningEvents.length > 0) {
    return currentlyRunningEvents.map((ev) => ev.index);
  }
  // Show events that start earliest (can be multiple events that start at the same time)
  const sortedEvents = events.slice().sort((a, b) => a.startTime - b.startTime);
  return sortedEvents.flatMap((ev, i) => (ev.startTime === sortedEvents[0].startTime ? [i] : []));
}

export const actions = (set: StoreApi<State>['setState']) => {
  function batch(func: (state: State) => State) {
    unstable_batchedUpdates(() => {
      set((state) => func(state));
    });
  }

  return {
    receiveBaseData(
      newEvents: Array<EventStateWithOptions>,
      newCurrentEventIndices: Array<number>,
      hash: HashContent,
    ) {
      if (newEvents.length < 1) return;
      let currentEventIndices = cleanEventIndices(newEvents.length, newCurrentEventIndices);
      if (currentEventIndices.length === 0) {
        currentEventIndices = getDefaultCurrentEventIds(newEvents);
      }
      const eventOptions = newEvents[0].globalOptions;
      const currentEvents = currentEventIndices.map((index) => newEvents[index]);
      const isLive = isLiveNow(reduceEndTime(currentEvents));
      const time = isLive
        ? correctedNow() - reduceMin(currentEvents, (ev) => ev.options.liveDelay)
        : reduceStartTime(currentEvents);

      const startersById = new Map();
      for (const event of newEvents) {
        for (const starter of event.starters) {
          const entry = startersById.get(starter.id);
          if (entry != null) {
            entry.push(starter);
          } else {
            startersById.set(starter.id, [starter]);
          }
        }
      }

      const events = newEvents.map(({ changeable, globalOptions: eventOptions, ...ev }) => ev);
      const eventsChangeable = newEvents.map(({ changeable }) => changeable);
      const defaultSelectedTags = new Map(
        Object.entries(eventOptions.tagCategoriesConfig).map(([key, value]) => [
          key,
          value.default,
        ]),
      );

      batch((state) => {
        let newState = produce(state, (d) => {
          d.currentEventIndices = currentEventIndices;
          d.events = castDraft(events);
          d.eventsChangeable = castDraft(eventsChangeable);
          d.startersById = startersById;
          d.timer.isLive = isLive;
          d.timer.isPlaying = isLive;
          d.options.mapType = eventOptions.mapType;
          d.options.unitType = eventOptions.unitType;
          d.options.timeMode = eventOptions.timeMode;
          d.options.showAllFlags = eventOptions.showAllFlags;
          d.options.pinToShadowTrack = eventOptions.pinToShadowTrack;
          d.options.largeMode = eventOptions.largeMode;
        });
        newState = applyHashOverrides(newState, hash);
        newState = setTimerTime(newState, time);
        newState = setSelectedTags(newState, defaultSelectedTags, { overwriteDefault: true });

        return newState;
      });
    },

    receiveOptions(options: PlayerBaseOptions, hash: HashContent) {
      batch((state) => {
        let newState = produce(state, (d) => {
          d.eventId = options.eventId;
          Object.assign(d.options, options);
        });
        newState = applyHashOverrides(newState, hash);
        return newState;
      });
    },

    refreshFullEventData(newEvents: Array<EventStateWithOptions>) {
      if (newEvents.length < 1) return;

      const startersById = new Map();
      for (const event of newEvents) {
        for (const starter of event.starters) {
          const entry = startersById.get(starter.id);
          if (entry != null) {
            entry.push(starter);
          } else {
            startersById.set(starter.id, [starter]);
          }
        }
      }

      const events = newEvents.map(({ changeable, globalOptions, ...ev }) => ev);
      const eventsChangeable = newEvents.map(({ changeable }) => changeable);

      batch((state) => {
        return produce(state, (d) => {
          d.events = castDraft(events);
          d.eventsChangeable = castDraft(eventsChangeable);
          d.startersById = startersById;
        });
      });
    },

    receiveStarterTracks(eventId: string, starterTracks: Map<string, Array<Point>>) {
      batch((state) => {
        const eventIndex = state.eventsChangeable.findIndex((ev) => ev.eventId === eventId);
        if (eventIndex >= 0) {
          return produce(state, (d) => {
            d.eventsChangeable[eventIndex].starterTracks = starterTracks;
          });
        }
        return state;
      });
    },

    switchEvent(currentEventIndices: Array<number>) {
      batch((state) => switchEvent(state, currentEventIndices));
    },

    toggleSelectedEvent(eventIndex: number) {
      batch((state) => {
        if (state.currentEventIndices.includes(eventIndex)) {
          const newCurrentEventIndices = state.currentEventIndices.filter(
            (index) => index !== eventIndex,
          );
          if (newCurrentEventIndices.length > 0) {
            return switchEvent(state, newCurrentEventIndices);
          }
        } else {
          return switchEvent(state, [...state.currentEventIndices, eventIndex]);
        }
        return state;
      });
    },

    setHighlighted(highlighted: string | null) {
      batch((state) =>
        highlighted == null ||
        state.startersById.has(highlighted) ||
        state.events.some((ev) => ev.pois.some((p) => p.id === highlighted)) ||
        state.events.some((ev) => ev.tracks.some((p) => p.id === highlighted))
          ? produce(state, (d) => {
              d.timer.highlighted = highlighted;
            })
          : state,
      );
    },

    setHighlightedOffset(highlightedOffset: number) {
      batch((state) => {
        const currentEventIndex = state.currentEventIndices[0];
        const currentEvent =
          currentEventIndex != null ? state.events[currentEventIndex] : undefined;
        const shadowTrack = currentEvent?.chunkedShadowTrack;

        if (highlightedOffset >= 0 && shadowTrack != null) {
          const maxOffset = Math.max(0, shadowTrack.size() - 1) * SHADOW_TRACK_STEP;

          return produce(state, (d) => {
            d.timer.highlightedOffset = clamp(0, highlightedOffset, maxOffset);
          });
        }
        return produce(state, (d) => {
          d.timer.highlightedOffset = -1;
        });
      });
    },

    setSelected(selected: Array<string>) {
      batch((state) =>
        produce(state, (d) => {
          d.timer.selected = selected.filter(
            (s) =>
              state.startersById.has(s) ||
              state.events.some((ev) => ev.pois.some((poi) => poi.id === s)) ||
              state.events.some((ev) => ev.tracks.some((track) => track.id === s)),
          );
        }),
      );
    },

    setSelectedStartNumber(selectedStartNumbers: Array<string>) {
      batch((state) => {
        const currentSelected = state.timer.selected;
        const currentSelectedNonStarterIds = currentSelected.filter(
          (s) => !state.startersById.has(s),
        );
        const selectedStarterIds = selectedStartNumbers
          .map((startNumber) =>
            iteratorFind(
              state.startersById.entries(),
              ([, starters]: [string, Array<StarterType>]) =>
                starters.some((starter) => starter.startNumber === startNumber),
            ),
          )
          .filter((item) => item != null)
          .map(([starterId]) => starterId);

        return produce(state, (d) => {
          d.timer.selected = [...selectedStarterIds, ...currentSelectedNonStarterIds];
        });
      });
    },

    setSelectedTags(selectedTags: Map<string, Array<string>>) {
      batch((state) => setSelectedTags(state, selectedTags));
    },

    toggleSelectedStarter(starterId: string) {
      batch((state) => {
        const currentSelectedStarters = state.timer.selected;
        if (!currentSelectedStarters.includes(starterId)) {
          return produce(state, (d) => {
            d.timer.selected.push(starterId);
          });
        }
        return produce(state, (d) => {
          d.timer.selected = currentSelectedStarters.filter((id) => id !== starterId);
        });
      });
    },

    toggleSelectedTag(key: string, value: string) {
      batch((state) => {
        const flatTags = flattenTags(state.timer.selectedTags);
        const tagIsSelected = flatTags.some((item) => item[0] === key && item[1] === value);
        if (!tagIsSelected) {
          return produce(state, (d) => {
            d.timer.selectedTags = unflattenTags([...flatTags, [key, value]]);
          });
        }
        return produce(state, (d) => {
          d.timer.selectedTags = unflattenTags(
            flatTags.filter((item) => item[0] !== key || item[1] !== value),
          );
        });
      });
    },

    hide(ids: Array<string>) {
      batch((state) =>
        produce(state, (d) => {
          d.timer.hidden = union(state.timer.hidden, new Set(ids));
        }),
      );
    },

    unhide(ids: Array<string>) {
      batch((state) =>
        produce(state, (d) => {
          d.timer.hidden = difference(state.timer.hidden, new Set(ids));
        }),
      );
    },
    toggleHidden(id: string) {
      batch((state) =>
        produce(state, (d) => {
          d.timer.hidden = state.timer.hidden.has(id)
            ? difference(state.timer.hidden, new Set([id]))
            : union(state.timer.hidden, new Set([id]));
        }),
      );
    },

    pauseTimer() {
      batch((state) =>
        produce(state, (d) => {
          d.timer.isPlaying = false;
        }),
      );
    },

    resumeTimer() {
      batch((state) =>
        produce(state, (d) => {
          d.timer.isPlaying = true;
        }),
      );
    },

    toggleTimer() {
      batch((state) => {
        if (reduceEndTime(getCurrentEvents(state)) <= state.timer.time && !state.timer.isPlaying) {
          return produce(state, (d) => {
            d.timer.isPlaying = true;
            d.timer.time = reduceStartTime(getCurrentEvents(state));
          });
        }
        return produce(state, (d) => {
          d.timer.isPlaying = !state.timer.isPlaying;
        });
      });
    },

    setIsLive(isLive: boolean) {
      batch((state) =>
        produce(state, (d) => {
          d.timer.isLive = isLive;
        }),
      );
    },

    toggleTimerSpeed() {
      batch((state) => {
        const timerSpeeds = [1, 2, 4, 8, 16];
        const newSpeed = toggleThroughArray(timerSpeeds, state.timer.speed);
        return produce(state, (d) => {
          d.timer.speed = newSpeed;
        });
      });
    },

    setTimerSpeed(speed: number) {
      batch((state) => {
        const timerSpeeds = [1, 2, 4, 8, 16];
        const newSpeed = timerSpeeds.includes(speed) ? speed : state.timer.speed;
        return produce(state, (d) => {
          d.timer.speed = newSpeed;
        });
      });
    },

    setTimerTime(time: number) {
      batch((state) => setTimerTime(state, time));
    },

    /**
     * Records the camera on screen so the share dialog can put it into a link. Called once the map
     * settles, never per frame — see PlaybackPositionManager for why that matters.
     *
     * An unchanged camera must not produce a new state — that re-runs Map's auto-follow effect,
     * which records the camera again.
     */
    setCurrentViewport(viewport: { longitude: number; latitude: number; zoom: number }) {
      batch((state) => {
        const current = state.currentViewport;
        if (
          current != null &&
          current.longitude === viewport.longitude &&
          current.latitude === viewport.latitude &&
          current.zoom === viewport.zoom
        ) {
          return state;
        }
        return produce(state, (d) => {
          d.currentViewport = viewport;
        });
      });
    },

    forwardTimerTime(timeDiff: number) {
      batch((state) => {
        const currentEvents = getCurrentEvents(state);
        const endTime = reduceEndTime(currentEvents);
        if (state.timer.time >= endTime) {
          return produce(state, (d) => {
            d.timer.isPlaying = false;
          });
        }
        const stepsPerMs =
          state.timer.speed * reduceMin(currentEvents, (ev) => ev.options.replaySpeedup);
        let newTime = state.timer.time + stepsPerMs * timeDiff;
        if (newTime > endTime) {
          newTime = endTime;
        }
        return setTimerTime(state, newTime);
      });
    },

    setGeolocationStatus(status: GeolocationStatus) {
      batch((state) =>
        produce(state, (d) => {
          d.timer.geolocation.status = status;
        }),
      );
    },

    setOwnLocation(latlng: LatLngOpacity | null) {
      batch((state) =>
        produce(state, (d) => {
          d.timer.geolocation.latlng = latlng;
        }),
      );
    },

    setMapType(mapType: MapType) {
      batch((state) =>
        produce(state, (d) => {
          d.options.mapType = mapType;
        }),
      );
    },

    setUnitType(unitType: UnitType) {
      batch((state) =>
        produce(state, (d) => {
          d.options.unitType = unitType;
        }),
      );
    },

    setShowAllFlags(showAllFlags: boolean) {
      batch((state) =>
        produce(state, (d) => {
          d.options.showAllFlags = showAllFlags;
        }),
      );
    },

    setLargeMode(largeMode: boolean) {
      batch((state) =>
        produce(state, (d) => {
          d.options.largeMode = largeMode;
        }),
      );
    },

    setOverrideFlagContent(overrideFlagContent: null | FlagContent) {
      batch((state) =>
        produce(state, (d) => {
          d.options.overrideFlagContent = overrideFlagContent;
        }),
      );
    },

    setTimeMode(timeMode: TimeMode) {
      batch((state) =>
        produce(state, (d) => {
          d.options.timeMode = timeMode;
        }),
      );
    },

    toggleTimeMode() {
      batch((state) =>
        produce(state, (d) => {
          d.options.timeMode = toggleThroughArray(TIME_MODES, state.options.timeMode);
        }),
      );
    },

    setPinToShadowTrack(pinToShadowTrack: PinToShadowTrackMode) {
      batch((state) =>
        produce(state, (d) => {
          d.options.pinToShadowTrack = state.events.every((ev) => ev.modules.projection.enabled)
            ? PinToShadowTrackMode.FORCED
            : pinToShadowTrack;
        }),
      );
    },

    setElevationShowProgress(value: boolean | null) {
      batch((state) =>
        produce(state, (d) => {
          d.options.elevationShowProgress = value;
        }),
      );
    },

    setElevationShowGrid(value: boolean | null) {
      batch((state) =>
        produce(state, (d) => {
          d.options.elevationShowGrid = value;
        }),
      );
    },

    setElevationShowAxis(value: boolean | null) {
      batch((state) =>
        produce(state, (d) => {
          d.options.elevationShowAxis = value;
        }),
      );
    },

    setElevationShowPOI(value: boolean | null) {
      batch((state) =>
        produce(state, (d) => {
          d.options.elevationShowPOI = value;
        }),
      );
    },
  };
};
